import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Notification, NotificationSounds, NotificationSound, NotificationType } from './notifications.models';
import { Subject } from 'rxjs';
import { throttleTime, tap, takeUntil } from 'rxjs/operators';
import * as fromNotification from './notifications.actions';
import { TranslateService } from '@ngx-translate/core';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  destroy$ = new Subject<void>();
  audioEvents: { [x: string]: Subject<void> } = {};
  audioElements: { [x: string]: HTMLAudioElement } = {};

  constructor(
    private store$: Store<any>,
    private translateService: TranslateService,
    private userContextService: UserContextService,
    private businessUnitsApiService: OrgBusinessUnitsApiService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.audioEvents = null;
    this.audioElements = null;
  }

  addNotification(notification: Notification): void {
    if (notification.messageType == NotificationType.MassCommunication && !!notification.fromName) {
      try {
        const fromData = JSON.parse(notification.fromName);
        const businessUnitUId = fromData.businessUnitUId;
        const userContext = this.userContextService.getUserContext();
        if (userContext.organizationUId !== notification.organizationUId) {
          return;
        }
        const userBusinessUnitUId = userContext.businessUnitUId;
        if (!!userBusinessUnitUId) {
          this.businessUnitsApiService.getOrgBusinessUnitsByUId(userBusinessUnitUId).subscribe(units => {
            if (units?.some(u => u.uId === businessUnitUId)) {
              this.store$.dispatch(fromNotification.addUnreadNotification({ notification }));
            }
          });
        } else if (userContext.needsBusinessUnit) {
          return;
        } else {
          this.store$.dispatch(fromNotification.addUnreadNotification({ notification }));
        }
      } catch (e) {}
    } else {
      this.store$.dispatch(fromNotification.addUnreadNotification({ notification }));
    }
  }

  playNotificationSound(soundUrl: string): void {
    // we will create a series of audio event observables based
    // on the sound. this way we can throttle requests to play the same sound
    // without dropping unique sounds.
    if (!soundUrl) {
      return;
    }

    let audioEvents$ = this.audioEvents[soundUrl] as Subject<void>;

    if (!audioEvents$) {
      // create the audio element
      this.audioElements[soundUrl] = new Audio(soundUrl);
      audioEvents$ = new Subject();
      audioEvents$
        .pipe(
          throttleTime(500),
          takeUntil(this.destroy$),
          tap(() => {
            this.audioElements[soundUrl]?.play();
          })
        )
        .subscribe();
    }

    audioEvents$.next();
  }

  getNotificationSounds(): NotificationSound[] {
    const translatedNotificationSounds: NotificationSound[] = NotificationSounds.map(s => {
      return { displayName: this.translateService.instant(s.displayName), url: s.url };
    }).sort((a, b) => {
      return a.displayName > b.displayName ? 1 : -1;
    });

    translatedNotificationSounds.unshift({ displayName: this.translateService.instant('NOTIFICATIONS.SOUND-NO-SOUND'), url: null });

    return translatedNotificationSounds;
  }
}
